import { ControlledRadio, ControlledTextField } from '@components/Form';
import { ReadField } from '@components/ReadField';
import type { GlobalLimitedCostData } from '@gql/graphql';
import { ChapterEnum } from '@gql/graphql';
import { cnConsta, FieldLabel, Grid, GridItem, RadioGroup, Tooltip } from '@uikit';
import { CALCULATING_PARTICIPATION_VARIANTS } from 'pages/Project/UerProject/Calculation/commonData';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Control, UseFormGetValues } from 'react-hook-form';

import Styles from '../BimCalculationLimitedEditModal.module.css';
import type { ChapterItem, TabItem } from '../commonData';
import { CHAPTER_ITEMS, CreatingTypes, invisibleStyle, TabsIds } from '../commonData';

import { BimCreateLimitedNameSelect } from './BimCreateLimitedNameSelect/BimCreateLimitedNameSelect';
import { BimLimitedCostTypeSelect } from './BimLimitedCostTypeSelect/BimLimitedCostTypeSelect';

export const COMPONENT_NAME = 'TextField' as const;
export const cnTextField = cnConsta(COMPONENT_NAME);

const getItemLabel = (item: { label: string }) => item.label;

interface IBimCalculationLimitedModalCommon {
    activeTab: TabItem;
    calculationId?: number;
    creatingType: CreatingTypes;
    isAdditionalTransport?: boolean;
    control: Control;
    globalLimitedCost: GlobalLimitedCostData;
    selectedChapter: ChapterItem;
    onSelectedChapterChange: ({ value }: { value: ChapterItem }) => void;
    isCreateModal?: boolean;
    isEditModal?: boolean;
    isViewModal?: boolean;
    globalOtherName?: string;
    getValues: UseFormGetValues<Record<string, any>>;
}

export const BimCalculationLimitedModalCommonTab: React.FC<IBimCalculationLimitedModalCommon> = (props) => {
    const {
        activeTab,
        calculationId,
        creatingType,
        isAdditionalTransport,
        control,
        globalLimitedCost,
        selectedChapter,
        onSelectedChapterChange,
        isCreateModal,
        isEditModal,
        isViewModal,
        globalOtherName,
        getValues,
    } = props;

    const refTooltip = useRef(null);
    const isCreatingFromCatalog = creatingType === CreatingTypes.BOOK;

    const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);

    const commonTabStyle = useMemo(
        () => (activeTab.id === TabsIds.COMMON ? undefined : invisibleStyle),
        [activeTab.id],
    );
    const typeSelectDisabled = useMemo(() => Boolean(globalLimitedCost?.id), [globalLimitedCost?.id]);
    const filteredChapterItems = useMemo(
        () => (!isAdditionalTransport ? CHAPTER_ITEMS.filter(({ id }) => id !== ChapterEnum.Chapter1_7).reverse() : []),
        [isAdditionalTransport],
    );

    const isHasCalculatingParticipationDisabled = useMemo(
        () => (isAdditionalTransport ? false : isCreatingFromCatalog),
        [isAdditionalTransport, isCreatingFromCatalog],
    );

    const handleMouseMoveSelect = useCallback(() => {
        setIsOpenTooltip(true);
    }, []);
    const handleMouseLeaveSelect = useCallback(() => {
        setIsOpenTooltip(false);
    }, []);

    return (
        <Grid style={commonTabStyle}>
            {(isViewModal || (isCreatingFromCatalog && isEditModal)) && (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={globalOtherName} label="Наименование" />
                </GridItem>
            )}
            {creatingType === CreatingTypes.MANUAL && !isViewModal && (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="name"
                        label="Наименование"
                        placeholder="Введите"
                        type="textarea"
                        control={control}
                        required
                        rules={{
                            required: 'Обязательное поле',
                            maxLength: {
                                value: 255,
                                message: 'Не более 255 символов',
                            },
                        }}
                        isTooltipHidden={!!commonTabStyle}
                        tooltipSize="m"
                    />
                </GridItem>
            )}
            {isCreatingFromCatalog && isCreateModal && (
                <GridItem className={Styles.marginBlock}>
                    <BimCreateLimitedNameSelect
                        control={control}
                        calculationId={calculationId}
                        refTooltip={refTooltip}
                        onMouseMoveSelect={handleMouseMoveSelect}
                        onMouseLeaveSelect={handleMouseLeaveSelect}
                        isTooltipHidden={!!commonTabStyle}
                        isAdditionalTransport={isAdditionalTransport}
                    />
                    {!commonTabStyle && globalLimitedCost?.name && isOpenTooltip && (
                        <Tooltip size="l" direction="upCenter" anchorRef={refTooltip}>
                            {globalLimitedCost.name}
                        </Tooltip>
                    )}
                </GridItem>
            )}
            {!isViewModal ? (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="key"
                        label="Код"
                        placeholder="Введите"
                        type="textarea"
                        control={control}
                        rules={{
                            maxLength: {
                                value: 255,
                                message: 'Не более 255 символов',
                            },
                        }}
                        isTooltipHidden={!!commonTabStyle}
                        tooltipSize="m"
                    />
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField value={getValues('key')} label="Код" />
                </GridItem>
            )}
            {(isEditModal && (!isCreatingFromCatalog || isAdditionalTransport)) || isCreateModal ? (
                <GridItem className={Styles.marginBlock}>
                    <div
                        className={cnTextField({
                            labelPosition: 'left',
                            size: 's',
                            view: 'default',
                            width: 'full',
                            lineHeight: 'm',
                        })}
                    >
                        <FieldLabel
                            size="s"
                            className={cn(cnTextField('Label', ['TextField-Label']), Styles.labelMaxWidth)}
                        >
                            Процент лимитированных затрат в СМР
                        </FieldLabel>
                        <ControlledRadio
                            items={CALCULATING_PARTICIPATION_VARIANTS}
                            control={control}
                            name="hasCalculatingParticipation"
                            direction="row"
                            textView="secondary"
                            textSize="s"
                            className={Styles.RadioGroupSmall}
                            disabled={isHasCalculatingParticipationDisabled}
                        />
                    </div>
                </GridItem>
            ) : (
                <GridItem className={Styles.negativeM}>
                    <ReadField
                        classSecondText={Styles.flexCenter}
                        value={getValues('hasCalculatingParticipation')?.label}
                        label="Процент лимитированных затрат в СМР"
                    />
                </GridItem>
            )}
            {(isEditModal && isCreatingFromCatalog) || isViewModal || isAdditionalTransport ? (
                <GridItem className={`${Styles.marginTop} ${Styles.negativeM}`}>
                    <ReadField value={selectedChapter?.label} label="Глава в Расчете" />
                </GridItem>
            ) : (
                <GridItem className={Styles.marginBlock}>
                    <div
                        className={cnTextField({
                            labelPosition: 'left',
                            size: 's',
                            view: 'default',
                            width: 'full',
                            lineHeight: 'm',
                        })}
                    >
                        <FieldLabel size="s" className={cnTextField('Label', ['TextField-Label'])}>
                            Глава в Расчете
                        </FieldLabel>
                        <RadioGroup
                            name="selectedChapter"
                            direction="row"
                            items={filteredChapterItems}
                            value={selectedChapter}
                            getItemLabel={getItemLabel}
                            onChange={onSelectedChapterChange}
                            className={`${Styles.RadioGroup} ${Styles.RadioGroupSmall}`}
                            disabled={isCreatingFromCatalog}
                        />
                    </div>
                </GridItem>
            )}
            <GridItem
                className={`${
                    isViewModal || (isCreatingFromCatalog && isEditModal)
                        ? `${Styles.marginTop} ${Styles.negativeM}`
                        : `${Styles.marginBlock}`
                }`}
            >
                <BimLimitedCostTypeSelect
                    control={control}
                    calculationId={calculationId}
                    isViewMode={isViewModal || (isCreatingFromCatalog && isEditModal)}
                    isTooltipHidden={!!commonTabStyle}
                    isDisabled={typeSelectDisabled || isCreatingFromCatalog || isEditModal}
                />
            </GridItem>

            {isViewModal ? (
                <GridItem className={`${Styles.marginTop} ${Styles.negativeM}`}>
                    <ReadField value={getValues('rationale')} label="Обоснование" />
                </GridItem>
            ) : (
                <GridItem className={Styles.marginBlock}>
                    <ControlledTextField
                        name="rationale"
                        label="Обоснование"
                        placeholder="Введите"
                        type="textarea"
                        control={control}
                        required
                        rules={{
                            required: 'Обязательное поле',
                            maxLength: {
                                value: 512,
                                message: 'Не более 512 символов',
                            },
                        }}
                        isTooltipHidden={!!commonTabStyle}
                        tooltipSize="m"
                    />
                </GridItem>
            )}
        </Grid>
    );
};

BimCalculationLimitedModalCommonTab.displayName = 'BimCalculationLimitedModalCommonTab';
